import React from "react"
import { useTournamentStanding } from "@/graphql/hooks/useTournamentStanding"
import { useTournamentRound } from "@/graphql/hooks/useTournamentRound"
import { gameRaces } from "@/lib/constants"
import { COLORS } from "./constants"
import type {
  ChartConfigEntry,
  RaceCount,
  RaceMatrixData,
  RacePerformanceStat,
  Tournament,
  TournamentPlayer,
} from "./types"

/**
 * Junta toda la data derivada (rondas, mesas, matriz raza vs raza,
 * distribución y rendimiento) que consume TournamentRaceStats, para que el
 * componente principal se dedique solo a componer la UI.
 */
export const useRaceStatsData = (tournament: Tournament, players: TournamentPlayer[]) => {
  const [selectedRoundId, setSelectedRoundId] = React.useState<number>(0)

  // Get all rounds for dropdown
  const availableRounds = React.useMemo(() => {
    if (!tournament.TournamentRounds?.length) return []
    return [...tournament.TournamentRounds]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .filter((r) => r.statusId === 2)
  }, [tournament.TournamentRounds])

  // Get latest round for performance data
  const latestRound = React.useMemo(() => {
    if (!tournament.TournamentRounds?.length) return null
    return tournament.TournamentRounds.reduce((latest, round) =>
      round.sortOrder > latest.sortOrder && round.statusId === 2 ? round : latest,
    )
  }, [tournament.TournamentRounds])

  // Set default round when available
  React.useEffect(() => {
    if (latestRound && selectedRoundId === 0) {
      setSelectedRoundId(latestRound.id)
    }
  }, [latestRound, selectedRoundId])

  // Get standing data for performance analysis
  const { standing, loading: standingLoading } = useTournamentStanding({
    roundId: selectedRoundId || latestRound?.id || 0,
    drop: false,
  })
  // Get round details including matches for race vs race analysis
  const { round: selectedRound } = useTournamentRound(selectedRoundId || latestRound?.id || 0)

  // Create race lookup from game data
  const raceMap = React.useMemo(() => {
    const map = new Map<number, string>()
    tournament.Game?.RaceGames?.forEach((raceGame) => {
      map.set(raceGame.raceId, raceGame.Race.name)
    })
    return map
  }, [tournament.Game?.RaceGames])

  // Get available tables from matches
  const [selectedTable, setSelectedTable] = React.useState<number[]>([])

  const availableTables = React.useMemo(() => {
    if (!selectedRound?.TournamentMatches?.length) return []
    const tables = selectedRound.TournamentMatches.map((match) => match.table)
      .filter((table, index, arr) => table && arr.indexOf(table) === index)
      .sort((a, b) => Number(a) - Number(b))
    return tables
  }, [selectedRound?.TournamentMatches])

  const raceMatrix: RaceMatrixData = React.useMemo(() => {
    if (!selectedRound?.TournamentMatches?.length) {
      return { matrix: {}, summary: [] }
    }

    const matrix: RaceMatrixData["matrix"] = {}

    const getRaceName = (id?: number) => raceMap.get(id!) || "Sin raza"

    // Si no hay mesas seleccionadas, considerar todas
    const tablesToInclude = selectedTable.length === 0 ? availableTables : selectedTable

    selectedRound.TournamentMatches.filter((m) => tablesToInclude.includes(m.table)).forEach(
      (match) => {
        if (!match.Player?.Person?.id || !match.Opponent?.Person?.id) return

        const race1 = getRaceName(match.Player.raceId)
        const race2 = getRaceName(match.Opponent.raceId)

        if (race1 === "Sin raza" || race2 === "Sin raza") return

        // Inicializar si no existe
        if (!matrix[race1]) matrix[race1] = {}
        if (!matrix[race1][race2]) matrix[race1][race2] = { wins: 0, losses: 0, draws: 0, total: 0 }
        if (!matrix[race2]) matrix[race2] = {}
        if (!matrix[race2][race1]) matrix[race2][race1] = { wins: 0, losses: 0, draws: 0, total: 0 }

        // Registrar resultado
        if (match.winnerId === match.playerId) {
          matrix[race1][race2].wins++
          matrix[race1][race2].total++
          matrix[race2][race1].losses++
          matrix[race2][race1].total++
        } else if (match.winnerId === match.opponentId) {
          matrix[race1][race2].losses++
          matrix[race1][race2].total++
          matrix[race2][race1].wins++
          matrix[race2][race1].total++
        } else if (match.draw) {
          matrix[race1][race2].draws++
          matrix[race1][race2].total++
          matrix[race2][race1].draws++
          matrix[race2][race1].total++
        }
      },
    )

    // Calcular resumen por raza
    const summary = Object.entries(matrix)
      .map(([race, opponents]) => {
        let wins = 0,
          total = 0
        Object.values(opponents).forEach((v) => {
          wins += v.wins
          total += v.total
        })
        const winRate = total > 0 ? (wins / total) * 100 : 0
        return { race, wins, total, winRate: Number(winRate.toFixed(1)) }
      })
      .sort((a, b) => b.winRate - a.winRate) // Ordenar del mejor al peor

    return { matrix, summary }
  }, [selectedRound?.TournamentMatches, selectedTable, raceMap, availableTables])

  // Get game name from tournament (you might need to adjust this based on your data structure)
  const getGameName = React.useMemo(() => {
    if (tournament.Game.id === 1) {
      return "imperio"
    } else if (tournament.Game.id === 4) {
      return "primer-bloque"
    } else if (tournament.Game.id === 11) {
      return "furia-extendido"
    } else if (tournament.Game.id === 6) {
      return "primera-era"
    }
  }, [tournament])

  // Check if game should group races by edition
  const shouldGroupRaces = React.useMemo(() => {
    return getGameName === "primer-bloque" || getGameName === "primera-era"
  }, [getGameName])

  // Get edition name for a race
  const getRaceEdition = React.useMemo(() => {
    if (!shouldGroupRaces) return () => null

    const gameData = gameRaces[getGameName as keyof typeof gameRaces]
    if (Array.isArray(gameData)) return () => null

    const editionMap = new Map<string, string>()
    Object.entries(gameData).forEach(([edition, races]) => {
      races.forEach((race) => {
        editionMap.set(race.name.toLowerCase(), edition)
      })
    })

    return (raceName: string) => editionMap.get(raceName.toLowerCase()) || null
  }, [shouldGroupRaces, getGameName])

  // Calculate edition statistics (for groupable games)
  const editionStats: RaceCount[] = React.useMemo(() => {
    if (!shouldGroupRaces) return []

    const stats = new Map<string, number>()
    const activePlayers = players?.filter((player) => !player.raceId || player.raceId > 0)

    activePlayers?.forEach((player) => {
      const raceName = raceMap.get(player.raceId) || "Sin raza"

      if (raceName !== "Sin raza") {
        const edition = getRaceEdition(raceName)
        if (edition) {
          // Capitalize first letter of each word in edition name
          const editionName = edition
            .split("-")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ")
          stats.set(editionName, (stats.get(editionName) || 0) + 1)
        }
      } else {
        stats.set("Sin raza", (stats.get("Sin raza") || 0) + 1)
      }
    })

    return Array.from(stats.entries())
      .map(([name, count]) => ({
        name,
        count,
        percentage: ((count / (activePlayers?.length || 1)) * 100).toFixed(1),
      }))
      .sort((a, b) => b.count - a.count)
  }, [players, raceMap, shouldGroupRaces, getRaceEdition])

  // Calculate individual race statistics (always show individual races)
  const raceStats: RaceCount[] = React.useMemo(() => {
    const stats = new Map<string, number>()
    const activePlayers = players?.filter((player) => !player.raceId || player.raceId > 0)

    activePlayers?.forEach((player) => {
      const raceName = raceMap.get(player.raceId) || "Sin raza"
      stats.set(raceName, (stats.get(raceName) || 0) + 1)
    })

    return Array.from(stats.entries())
      .map(([name, count]) => ({
        name,
        count,
        percentage: ((count / (activePlayers?.length || 1)) * 100).toFixed(1),
      }))
      .sort((a, b) => b.count - a.count)
  }, [players, raceMap])

  // Chart configuration for races
  const raceChartConfig = React.useMemo(() => {
    const config: Record<string, ChartConfigEntry> = {}
    raceStats.forEach((race, index) => {
      config[race.name] = {
        label: race.name,
        color: COLORS[index % COLORS.length],
      }
    })
    return config
  }, [raceStats])

  // Chart configuration for editions
  const editionChartConfig = React.useMemo(() => {
    const config: Record<string, ChartConfigEntry> = {}
    editionStats.forEach((edition, index) => {
      config[edition.name] = {
        label: edition.name,
        color: COLORS[index % COLORS.length],
      }
    })
    return config
  }, [editionStats])

  // Calculate race performance statistics
  const racePerformanceStats: RacePerformanceStat[] = React.useMemo(() => {
    if (!standing?.length) return []

    const performanceMap = new Map<string, Omit<RacePerformanceStat, "name">>()

    standing.forEach((entry) => {
      const raceName = raceMap.get(entry.TournamentPlayer?.raceId) || "Sin raza"
      const current = performanceMap.get(raceName) || {
        totalPoints: 0,
        players: 0,
        avgPoints: 0,
        totalOMW: 0,
        totalPGW: 0,
        totalOGW: 0,
        avgOMW: 0,
        avgPGW: 0,
        avgOGW: 0,
      }

      current.totalPoints += entry.points || 0
      current.players += 1
      current.totalOMW += parseFloat(entry.omw || "0")
      current.totalPGW += parseFloat(entry.pgw || "0")
      current.totalOGW += parseFloat(entry.ogw || "0")

      performanceMap.set(raceName, current)
    })

    return Array.from(performanceMap.entries())
      .map(([name, stats]) => ({
        name,
        ...stats,
        avgPoints: stats.totalPoints / stats.players,
        avgOMW: stats.totalOMW / stats.players,
        avgPGW: stats.totalPGW / stats.players,
        avgOGW: stats.totalOGW / stats.players,
      }))
      .sort((a, b) => b.avgPoints - a.avgPoints)
  }, [standing, raceMap])

  const totalPlayers = players?.length || 0
  const playersWithRaces = raceStats
    .filter((x) => x.name !== "Sin raza")
    .reduce((sum, race) => sum + race.count, 0)
  const playersWithoutRaces = raceStats
    .filter((x) => x.name === "Sin raza")
    .reduce((sum, race) => sum + race.count, 0)

  // Filtro de mesas por rango: seleccionar la mesa N selecciona también
  // todas las mesas menores (1..N); deseleccionar la mesa N deselecciona
  // también todas las mesas mayores (N en adelante).
  const toggleTable = (value: number) => {
    setSelectedTable((prev) => {
      const isSelected = prev.includes(value)
      if (isSelected) {
        return prev.filter((v) => v < value)
      }
      return availableTables.filter((t) => t <= value)
    })
  }

  return {
    selectedRoundId,
    setSelectedRoundId,
    availableRounds,
    selectedRound,
    standingLoading,
    selectedTable,
    availableTables,
    toggleTable,
    raceMatrix,
    shouldGroupRaces,
    editionStats,
    raceStats,
    raceChartConfig,
    editionChartConfig,
    racePerformanceStats,
    totalPlayers,
    playersWithRaces,
    playersWithoutRaces,
  }
}
